import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { setSuccess, clearErrors, setAlert } from '../redux/actions/alertActions';

// Components
import AdminTable from './AdminTable';
import CrudDialog from './CrudDialog';
import ConfirmationModal from './ConfirmationModal';

/**
 * One configuration table page: the table, its add / edit dialog and its
 * delete confirmation. Each admin page is a configuration of this.
 *
 * A record opens in the dialog with `renderForm(row, onSaved)` (row is null
 * when adding), or, for records the server cannot edit (hubs, badges),
 * `renderView(row)` behind a "View" button.
 */
class ConfigTablePage extends Component {
  constructor(props) {
    super(props);
    this.table = React.createRef();
    this.state = { dialog: null, confirmDelete: null, isDeleting: false };
  }

  componentWillUnmount() {
    this.props.clearErrors();
  }

  close = () => this.setState({ dialog: null });

  saved = () => {
    this.setState({ dialog: null });
    this.table.current?.reload();
  }

  confirmDelete = () => {
    const row = this.state.confirmDelete;
    this.setState({ isDeleting: true });
    this.props.remove(row).then(() => {
      this.setState({ isDeleting: false, confirmDelete: null, dialog: null });
      this.props.setSuccess(this.props.deleted(row));
      this.table.current?.reload();
    }).catch(error => {
      this.setState({ isDeleting: false });
      this.props.setError(error ?? 'Unable to delete this ' + this.props.singular + '.');
    });
  }

  render() {
    const { title, singular, renderForm, renderView, remove, deleteTitle, deleteBody, dialogWidth } = this.props;
    const { dialog } = this.state;
    const viewOnly = renderView != null;

    let dialogTitle = 'Add ' + singular;
    if (dialog?.row) dialogTitle = (viewOnly ? '' : 'Edit ') + (viewOnly ? this.props.viewTitle(dialog.row) : singular);

    const actions = [];
    if (viewOnly) actions.push({ label: 'View', onClick: (row) => this.setState({ dialog: { row } }) });
    else if (renderForm) actions.push({ label: 'Edit', onClick: (row) => this.setState({ dialog: { row } }) });
    if (remove) actions.push({ label: 'Delete', tone: 'danger', onClick: (row) => this.setState({ confirmDelete: row }) });
    const reload = () => this.table.current?.reload();

    return (
      <>
        <ConfirmationModal
          open={this.state.confirmDelete != null}
          onClose={() => this.setState({ confirmDelete: null })}
          onConfirm={this.confirmDelete}
          title={this.state.confirmDelete ? deleteTitle(this.state.confirmDelete) : ''}
          body={deleteBody}
          isLoading={this.state.isDeleting}
        />

        <CrudDialog
          open={dialog != null}
          onClose={this.close}
          eyebrow={title}
          title={dialogTitle}
          width={dialogWidth}
          onDelete={dialog?.row && remove ? () => this.setState({ confirmDelete: dialog.row }) : null}
        >
          {dialog != null && (dialog.row && viewOnly ? renderView(dialog.row) : renderForm(dialog.row, this.saved))}
        </CrudDialog>

        <AdminTable
          ref={this.table}
          title={title}
          singular={singular}
          columns={this.props.columns}
          load={this.props.load}
          idOf={this.props.idOf}
          searchText={this.props.searchText}
          searchPlaceholder={this.props.searchPlaceholder}
          onAdd={renderForm ? () => this.setState({ dialog: { row: null } }) : null}
          addLabel={this.props.addLabel}
          rowActions={(row) => actions.concat(this.props.moreActions ? this.props.moreActions(row, { reload, setError: this.props.setError, setSuccess: this.props.setSuccess }) : [])}
        />
      </>
    );
  }
}

ConfigTablePage.propTypes = {
  title: PropTypes.string.isRequired,
  singular: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  load: PropTypes.func.isRequired,
  idOf: PropTypes.func.isRequired,
  searchText: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  addLabel: PropTypes.string,
  /** (row | null, onSaved) => the entity's form */
  renderForm: PropTypes.func,
  /** row => its details, for records that cannot be edited */
  renderView: PropTypes.func,
  viewTitle: PropTypes.func,
  dialogWidth: PropTypes.number,
  /** (row, { reload, setError, setSuccess }) => extra row buttons, after Edit and Delete */
  moreActions: PropTypes.func,
  /** row => Promise; omit when records cannot be deleted */
  remove: PropTypes.func,
  deleteTitle: PropTypes.func,
  deleteBody: PropTypes.node,
  deleted: PropTypes.func,
  setError: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch => ({
  setError: (msg) => {
    dispatch(clearErrors());
    setAlert(dispatch, msg);
  },
  setSuccess: (msg) => setSuccess(dispatch, msg),
  clearErrors: () => dispatch(clearErrors())
});

export default connect(null, mapDispatchToProps)(ConfigTablePage);
